/**
 * ChevronToggle Component
 *
 * A reusable expand/collapse toggle button with animated chevron.
 * Used in Schedule views and Accordion components for consistent UX.
 *
 * Features:
 * - Smooth 180deg rotation animation
 * - Two variants: "toggle" (green button) and "minimal" (icon only)
 * - Full accessibility with aria-expanded and aria-label
 * - Keyboard support (Enter/Space)
 * - iconOnly mode for use inside other buttons
 *
 * @example
 * ```tsx
 * // Scheduler style (green button)
 * <ChevronToggle
 *   isOpen={!isCollapsed}
 *   onClick={() => toggleCollapse(id)}
 *   variant="toggle"
 * />
 *
 * // Accordion style (minimal)
 * <ChevronToggle
 *   isOpen={isExpanded}
 *   onClick={onToggle}
 *   variant="minimal"
 * />
 *
 * // Icon-only mode (inside another button)
 * <ChevronToggle
 *   isOpen={isExpanded}
 *   onClick={onToggle}
 *   iconOnly
 * />
 * ```
 */

import React, { useCallback } from "react";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { ChevronToggleProps } from "./types";
import styles from "./ChevronToggle.module.css";

export const ChevronToggle = React.forwardRef<
  HTMLButtonElement | HTMLSpanElement,
  ChevronToggleProps
>(
  (
    {
      isOpen,
      onClick,
      variant = "toggle",
      iconOnly = false,
      disabled = false,
      expandedLabel = "Kollaps",
      collapsedLabel = "Utvid",
      "aria-controls": ariaControls,
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!disabled) {
          onClick();
        }
      },
      [disabled, onClick],
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled) {
            onClick();
          }
        }
      },
      [disabled, onClick],
    );

    // Icon-only mode: render just the icon with rotation styles (for use inside buttons)
    if (iconOnly) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={cx(styles.iconOnly, isOpen && styles.open, className)}
          aria-hidden
          data-testid={testId}
        >
          <Icon name="chevronDown" size="sm" aria-hidden />
        </span>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={cx(
          styles.chevronToggle,
          styles[variant],
          isOpen && styles.open,
          disabled && styles.disabled,
          className,
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-label={isOpen ? expandedLabel : collapsedLabel}
        aria-controls={ariaControls}
        data-testid={testId}
      >
        <Icon name="chevronDown" size="lg" aria-hidden />
      </button>
    );
  },
);

ChevronToggle.displayName = "ChevronToggle";

export default ChevronToggle;
